import React, { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { programsApi } from 'api';
import { Application } from 'types/Application';
import { Spinner } from 'components/_new/Spinner';
import { MessageBox } from 'components/_new/MessageBox';
import BusinessCard from './BusinessCard';

export type BusinessCardIdentifierScope = 'program-admin' | 'program-coordinator' | 'none';

interface BusinessCardIdentifierProps {
	visible: boolean;
	programId: number;
	applicationId: number;
	scope: BusinessCardIdentifierScope;
	initialData?: Application;
	additionalAnswers?: React.ComponentProps<typeof BusinessCard>['additionalAnswers'];
}

const BusinessCardIdentifier: FC<BusinessCardIdentifierProps> = ({
	visible,
	programId,
	applicationId,
	scope,
	initialData,
	additionalAnswers,
}) => {
	const { t } = useTranslation();

	const shouldFetch = visible && scope !== 'none' && programId > 0 && applicationId > 0;

	const businessCardQuery = useQuery(
		['BUSINESS_CARD_IDENTIFIER', scope, programId, applicationId],
		() => {
			if (scope === 'program-admin') {
				return programsApi.getProgramApplicationBusinessCardAdmin({ programId, applicationId });
			}
			return programsApi.getProgramApplicationBusinessCardCoordinator({ programId, applicationId });
		},
		{
			enabled: shouldFetch,
		}
	);

	const applicationData = shouldFetch ? businessCardQuery.data : initialData;

	if (shouldFetch && businessCardQuery.isLoading) {
		return (
			<div className="flex justify-content-center py-4">
				<Spinner />
			</div>
		);
	}

	if (shouldFetch && businessCardQuery.isError) {
		return <MessageBox variant="warn" title={t('misc.error')} message={t('misc.genericError')} />;
	}

	if (!applicationData) {
		return null;
	}

	return <BusinessCard data={applicationData} additionalAnswers={additionalAnswers} />;
};

export default BusinessCardIdentifier;
